'use client';

import { useEffect, useRef } from 'react';
import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { readBlockCtaIntent, resolveBlockCtaHref } from '../../lib/resolveSectionHref';
import type { BlockRenderProps } from '../../types';
import { resolveHeroCopySide, resolveHeroDishSide } from '@web-studio/shared-schemas';

const REEL_MEDIA_KEYS = ['heroImage', 'heroImageSecondary', 'heroImageTertiary'] as const;

function collectReelFrames(block: BlockRenderProps['block']): {
  key: (typeof REEL_MEDIA_KEYS)[number];
  url: string;
}[] {
  const frames: { key: (typeof REEL_MEDIA_KEYS)[number]; url: string }[] = [];
  for (const key of REEL_MEDIA_KEYS) {
    const url = pickMediaUrl(block, key);
    if (url) frames.push({ key, url });
  }
  return frames;
}

/**
 * Full-viewport hero with a quiet multi-frame crossfade (2–3 photos).
 * Motion is CSS-driven so SSR / edge HTML match; React only pauses when the tab is hidden.
 */
export function HeroReelFade({
  block,
  locale,
  defaultLocale = locale,
  isActive,
  activeEditTarget,
  editable,
  headingLevel = 'h1',
  pages,
  siteMode,
  allBlocks,
}: BlockRenderProps) {
  const Heading = headingLevel;
  const eyebrow = pickBlockField(block.content, 'eyebrow', locale);
  const heading = pickBlockField(block.content, 'heading', locale);
  const subheading = pickBlockField(block.content, 'subheading', locale);
  const ctaPrimary = pickBlockField(block.content, 'ctaPrimary', locale);
  const ctaPrimaryIntent = readBlockCtaIntent(block.content, locale, {
    hrefFieldKey: 'ctaPrimaryHref',
    fallback: 'book',
  });
  const ctaPrimaryHref = resolveBlockCtaHref(ctaPrimaryIntent, {
    locale,
    defaultLocale,
    content: block.content,
    hrefFieldKey: 'ctaPrimaryHref',
    siteMode,
    pages,
    allBlocks,
  });
  const frames = collectReelFrames(block);
  const imageAlt = heading || eyebrow || 'Hero';
  const target = isActive ? activeEditTarget : null;
  const dishSide = resolveHeroDishSide(block.content);
  const copySide = resolveHeroCopySide(dishSide);
  const sectionRef = useRef<HTMLElement | null>(null);
  const animate = frames.length >= 2;

  useEffect(() => {
    if (!animate) return;
    const root = sectionRef.current;
    if (!root) return;

    const sync = () => {
      root.dataset.reelPaused = document.visibilityState === 'hidden' ? 'true' : 'false';
    };
    sync();
    document.addEventListener('visibilitychange', sync);
    return () => document.removeEventListener('visibilitychange', sync);
  }, [animate]);

  return (
    <section
      ref={sectionRef}
      className={['ws-hero-reel', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      data-dish-side={dishSide}
      data-copy-side={copySide}
      data-reel-count={String(Math.max(frames.length, 1))}
      data-reel-paused="false"
      id="top"
    >
      <div className="ws-hero-reel__stage" aria-hidden={frames.length === 0}>
        {frames.length > 0 ? (
          frames.map((frame, index) => {
            const src = buildSiteImageProxyUrl(frame.url, 1800, 78);
            const srcSet = buildSiteImageSrcSet(frame.url, [480, 768, 1200, 1600, 2000], 78);
            return (
              <EditableMedia
                key={frame.key}
                fieldKey={frame.key}
                editable={editable}
                activeEditTarget={target}
                className={[
                  'ws-hero-reel__photo',
                  index === 0 ? 'ws-hero-reel__photo--lead' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                src={src}
                srcSet={srcSet}
                sizes={SITE_IMAGE_SIZES.heroFullBleed}
                alt={index === 0 ? imageAlt : ''}
                width={1600}
                height={900}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : undefined}
                decoding="async"
              />
            );
          })
        ) : (
          <div className="ws-hero-reel__placeholder" aria-hidden />
        )}
        <div className="ws-hero-reel__veil" aria-hidden />
        <div className="ws-hero-reel__side-veil" aria-hidden />
      </div>
      <div id="about" className="ws-hero-reel__copy">
        <div className="ws-hero-reel__pocket">
          <EditableContent
            fieldKey="eyebrow"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-reel__eyebrow"
          >
            {eyebrow}
          </EditableContent>
          <EditableContent
            as={Heading}
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-reel__title"
          >
            {heading}
          </EditableContent>
          <EditableContent
            fieldKey="subheading"
            editable={editable}
            activeEditTarget={target}
            className="ws-hero-reel__lead"
          >
            {subheading}
          </EditableContent>
          {ctaPrimary ? (
            <EditableContent
              as="a"
              fieldKey="ctaPrimary"
              editable={editable}
              activeEditTarget={target}
              className="ws-hero-reel__cta"
              href={ctaPrimaryHref}
            >
              {ctaPrimary}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
